// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { Action } from "redux"
import { AttachTagRequest, AttachTagResponse, IdList } from "generated/wisetime/user/timeline/v4/timeline_pb"
import * as Actions from "../actions"
import { grpc } from "app/rpc/executor"
import { timelineClient } from "app/rpc/client"
import * as TimelineSlice from "../slices/timeline"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { TagRef } from "../../../generated/wisetime/user/type/tag_ref_pb"
import { RootState } from "../../../app/store"
import { notUndefined } from "../../../lib/utils"

export type AttachTagRpc = (request: AttachTagRequest) => Observable<AttachTagResponse>

export const attachTagFulfilled = (
  attachTagRequest: AttachTagRequest,
  tag: TagRef,
  state?: RootState,
): Observable<Action> => {
  const actions: Action[] = []
  const tagCategoryId = state && state.tags.tagsCache[tag.getId()]?.tagCategoryId
  const timeGroupsIds = attachTagRequest.getTimeGroupIds() ?? new IdList().setIdsList([])
  const timeRowIds = attachTagRequest.getTimeRowIds() ?? new IdList().setIdsList([])
  const timeRowsHasCategory = tagCategoryId ? timeRowIds.getIdsList()
    .map(rowId => state.timeline.timeRowsCache[rowId])
    .filter(row => row?.tagCategoriesList.some(category => category.id === tagCategoryId))
    .map(row => row?.id)
    .filter(notUndefined) : []
  const timeGroupHasCategory = tagCategoryId ? timeGroupsIds.getIdsList()
    .map(groupId => state.timeline.timeGroupsCache[groupId])
    .filter(timeGroup => timeGroup?.tagCategoriesList.some(category => category.id === tagCategoryId))
    .map(timeGroup => timeGroup?.id)
    .filter(notUndefined)[0] : undefined
  if (attachTagRequest.hasTimeRowIds()) {
    actions.push(TimelineSlice.attachTimeRowsTag({
      timeRowIds,
      tag,
    }))
  }
  if (attachTagRequest.hasTimeGroupIds()) {
    actions.push(TimelineSlice.attachTimeGroupsTags({
      timeGroupsIds,
      tag,
    }))
  }
  if (timeRowsHasCategory.length > 0 && tagCategoryId) {
    actions.push(TimelineSlice.detachTagCategory({
      timeRowIds: timeRowsHasCategory,
      timeGroupId: "",
      tagCategoryId,
    }))
  }
  if (timeGroupHasCategory && tagCategoryId) {
    actions.push(TimelineSlice.detachTagCategory({
      timeRowIds: undefined,
      timeGroupId: timeGroupHasCategory,
      tagCategoryId,
    }))
  }
  return concat(actions)
}

export const attachTag = (attachTagRpc: AttachTagRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.attachTag.match),
      withLatestFrom(state$),
      mergeMap(([{ payload: { attachTagRequest, tag }}, state]) =>
        of(attachTagRequest)
          .pipe(
            mergeMap(attachTagRpc),
            mergeMap(_ => attachTagFulfilled(attachTagRequest, tag, state)),
            catchError(handleError),
          )
      ),
    )

export const attachTagRpc = (request: AttachTagRequest): Observable<AttachTagResponse> =>
  grpc(() => timelineClient.attachTag(request, null))

const handleError = (e: unknown) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return of(showSnackBar({
        message: t({ id: "msg_add_tag_failed_not_found", message: "Failed to add tag. Could not find the target time row or time group." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_add_tag_failed_permission_denied", message: "Failed to add tag. You do not have permission to add a tag to the time row or time group." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_add_tag_failed_precondition", message: "Failed to add tag. Time row may be out of sync." }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_add_tag_failed", message: "Failed to add tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default attachTag(attachTagRpc)
